import React, { useMemo, memo, useEffect, useReducer, useCallback, useRef, useState } from 'react';

import { Section, Select } from '@Sage/hoi-poi-ui';
import { normalizeFieldName } from '@Sage/ssm-utils';

import ForceForm, { validate, flatList, getDefaults } from 'components/ForceForm';
import adapt from 'components/ForceForm/lib/adapt';
import HyperDrawer from 'components/HyperDrawer';
import { showErrorToast } from 'lib/errors';
import { LANGUAGES } from 'lib/helpers';
import { getSchema } from 'lib/services/schemas';
import { getTable, createTable, updateTable, getList } from 'lib/services/valueLists';
import { useWeb5Context } from 'lib/Web5Context';
import { successToast } from 'utils/toast';

import AdviceRelatedFields from './AdviceRelatedFields';
import { getValueListFormProperties, VALUE_LIST_FORM_PROPERTIES } from './schema';
import integrationSectionStyles from './styles/integrationSection.styles';
import ValueListValues from './ValueListValues/index';
const FORM_SCHEMA = {
    required: ['name', 'description'],
    properties: VALUE_LIST_FORM_PROPERTIES,
    layout: {
        id: 'valueList',
        uiGrid: 'linearVertical',
        composition: [
            {
                uiType: 'section',
                uiGrid: 'linearVertical',
                label: 'cfm_label_info',
                expandable: true,
                composition: [
                    {
                        id: 'description',
                        label: 'cfm_label_list_description',
                        uiType: 'expandableComponent',
                        helpTip: 'cfm_label_values_list_name_help',
                        labelForExpand: ['cfm_label_view_more_lang', 'cfm_label_view_less_lang'],
                        composition: [
                            {
                                schemaId: 'description',
                                uiType: 'text',
                                label: 'cfm_label_list_description',
                                placeholder: 'cfm_label_spanish',
                            },
                            ...LANGUAGES.map((language) => ({
                                label: `cfm_label_${language.label}`,
                                schemaId: `description${language.key}`,
                                uiType: 'text',
                                placeholder: 'cfm_placeholder_type_value',
                            })),
                        ],
                    },
                    {
                        schemaId: 'name',
                        label: 'cfm_value_list_name',
                        uiType: 'text',
                        placeholder: 'cfm_placeholder_type_value',
                        helpTip: 'cfm_label_values_list_db_name_help',
                    },
                    {
                        label: 'cfm_label_apiv4_name',
                        schemaId: 'apiNameV4',
                        isReadOnly: true,
                        isHidden: true,
                        helpTip: 'cfm_label_values_list_apiv4_name_help',
                        override: {
                            create: {
                                isHidden: true,
                            },
                        },
                    },
                    {
                        schemaId: 'isEditable',
                        label: 'cfm_label_editable_by_user',
                        uiType: 'checkbox',
                        isHidden: false,
                    },
                ],
            },
        ],
    },
};

const initialState = {
    id: null,
    data: {},
    originalData: {},
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    isForDuplicate: false,
    valueListOrder: [],
    mappedValueListOrder: null,
    hasOrdered: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                id: action.id,
                originalData: {
                    isNew: !action.id,
                    ...getDefaults(action.schema),
                    ...(action.initialData || {}),
                },
                data: {
                    isNew: !action.id,
                    ...getDefaults(action.schema),
                    ...(action.initialData || {}),
                },
                validationData: {
                    isNew: !action.id,
                    ...getDefaults(action.schema),
                    ...(action.initialData || {}),
                },
                errors: {},
                isOpen: true,
                isLoading: true,
                isForDuplicate: action.isForDuplicate,
            };
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeIds':
            return {
                ...state,
                validationData: { ...state.data, ids: action.ids },
                originalData: { ...state.originalData, ids: action.originalIds },
            };
        case 'setHasOrdered':
            return {
                ...state,
                hasOrdered: true,
            };
        case 'changeData':
            const finalOriginalData = action.originalData || state.originalData;
            return {
                ...state,
                data: action.data || state.data,
                validationData: { ...action.data, ids: action.ids },
                originalData: { ...finalOriginalData, ids: action.originalIds },
            };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        case 'setLoading':
            return {
                ...state,
                isLoading: action.value,
            };
        default:
            throw new Error('No action provided');
    }
}

const FormValueList = ({
    onRef,
    refreshList,
    onSave,
    onCancel,
    onStack,
    setActiveRowId,
    timeZone,
    overrides,
    onUpdate,
    initialData,
    isSage = false,
}) => {
    const {
        t,
        permissions,
        overridePermissions,
        fieldsToHide = [],
        fieldsToAvoidExpand = [],
        isWeb,
    } = useWeb5Context({ overrides });
    const [stackIndex, setStackIndex] = useState(1);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [ids, setIds] = useState([]);
    const [originalIds, setOriginalIds] = useState([]);
    const [integrationFieldOptions, setIntegrationFieldOptions] = useState([]);

    const formFields = useRef();
    const valueSchema = useRef();

    const createFields = useRef([]);
    const updateFields = useRef({});
    const deleteFields = useRef([]);

    const order = useRef([]);
    const mappedOrder = useRef({});

    const overrideIsHidden = useCallback(
        (id) => Array.isArray(fieldsToHide) && fieldsToHide.includes(id),
        [fieldsToHide],
    );

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open({ id, isForDuplicate, isEditable }) {
                    if (state.isOpen) return;
                    // Resetting refs
                    valueSchema.current = null;
                    formFields.current = null;
                    createFields.current = [];
                    updateFields.current = {};
                    deleteFields.current = [];

                    setStackIndex(1);

                    const FINAL_SCHEMA = fieldsToHide.length
                        ? {
                              ...FORM_SCHEMA,
                              layout: {
                                  ...FORM_SCHEMA.layout,
                                  composition: [
                                      {
                                          ...FORM_SCHEMA.layout.composition[0],
                                          composition:
                                              FORM_SCHEMA.layout.composition[0].composition.map(
                                                  (field) => {
                                                      field.isHidden =
                                                          overrideIsHidden(
                                                              field.schemaId || field.id,
                                                          ) || field.isHidden;
                                                      if (fieldsToAvoidExpand?.length) {
                                                          if (
                                                              fieldsToAvoidExpand.includes(
                                                                  field.schemaId || field.id,
                                                              )
                                                          ) {
                                                              field.uiType = 'text';
                                                          }
                                                      }
                                                      return field;
                                                  },
                                              ),
                                      },
                                  ],
                              },
                          }
                        : FORM_SCHEMA;

                    dispatch({
                        type: 'init',
                        id,
                        isForDuplicate,
                        schema: FINAL_SCHEMA,
                        initialData,
                    });
                    formFields.current = JSON.parse(JSON.stringify(FINAL_SCHEMA));
                    formFields.current.properties = getValueListFormProperties({
                        allowPersistedTechnicalName: Boolean(id && !isForDuplicate),
                    });

                    if (!isForDuplicate && id) {
                        formFields.current.layout.composition[0].composition.forEach(
                            (component) => {
                                if (
                                    component.schemaId === 'name' ||
                                    component.schemaId === 'cfm_label_apiv4_name'
                                )
                                    component.isReadOnly = true;
                            },
                        );
                    }

                    if (
                        !fieldsToHide?.includes(
                            formFields.current.layout.composition[0].composition[2].schemaId,
                        )
                    ) {
                        formFields.current.layout.composition[0].composition[2].isHidden =
                            !overridePermissions && !permissions?.valueList?.show;
                    }

                    // Load integration field options
                    getList({ list: 'tblIntegrationValues' })
                        .then((response) => {
                            const options =
                                response?.data?.map((item) => ({
                                    value: item.id,
                                    label: item.value || item.description || item.name,
                                })) || [];
                            setIntegrationFieldOptions(options);
                        })
                        .catch((error) => {
                            console.error(error);
                        });

                    if (!id) {
                        return getSchema('customValuelist')
                            .then(({ data }) => {
                                valueSchema.current = data;
                                dispatch({ type: 'setLoading', value: false });
                            })
                            .catch((error) => {
                                console.error(error);
                                showErrorToast(t, error);
                            });
                    }

                    getTable(id)
                        .then(({ data }) => {
                            valueSchema.current = data.schema;
                            if (id && !isForDuplicate) {
                                formFields.current.layout.composition[0].composition[1].isReadOnly = true;
                                let hasValue = data['apiNameV4'];
                                if (!hasValue)
                                    data['apiNameV4'] = t('cfm_label_value_list_available_in_api');

                                formFields.current.layout.composition[0].composition[2].ishidden = false;
                                formFields.current.layout.composition[0].composition[2].uiType =
                                    hasValue ? 'text' : 'static';
                            }

                            if (isForDuplicate) {
                                data = {
                                    ...data,
                                    schemaId: null,
                                    isNew: true,
                                    name: `${data.name}_copy`,
                                    apiNameV4: null,
                                };
                            }

                            if (data) {
                                dispatch({ type: 'changeData', data, originalData: data });
                            }

                            dispatch({ type: 'setLoading', value: false });
                        })
                        .catch((error) => {
                            console.error(error);
                            showErrorToast(t, error);
                        });
                },
            });
    }, [
        overridePermissions,
        permissions?.valueList?.show,
        onRef,
        stackIndex,
        t,
        fieldsToHide.length,
        fieldsToAvoidExpand,
        overrideIsHidden,
        initialData,
        fieldsToHide,
        state?.isOpen,
    ]);

    const mapList = useCallback((list) => {
        return (
            list?.reduce((obj, current) => {
                obj[current.id] = current;
                return obj;
            }, {}) || {}
        );
    }, []);

    const updateOrder = useCallback(
        ({ values, id }) => {
            if (id) {
                delete mappedOrder.current[id];
                order.current = order.current?.filter((item) => item.id !== id) || [];
            } else {
                order.current = values;
                mappedOrder.current = mapList(values);
            }
        },
        [mapList],
    );

    const onCleaningProps = useCallback((data, properties) => {
        const keys = Object.keys(properties);
        return Object.keys(data).reduce((newData, key) => {
            keys.forEach((item) => {
                if (key === item) newData[key] = data[key];
            });

            return newData;
        }, {});
    }, []);

    const onChangeOrder = useCallback(
        (values) => {
            updateOrder({ values });

            createFields.current = values
                .filter((v) => v.isNew)
                .map(({ id, isNew, schema, isTouched, ...rest }) => {
                    let cleanData = adapt(rest, valueSchema.current);
                    cleanData = onCleaningProps(cleanData, valueSchema.current.properties);
                    return cleanData;
                });

            updateFields.current = values
                .filter((v) => !v.isNew)
                .reduce((obj, v) => {
                    const { isTouched, id, ...rest } = v;
                    let cleanData = adapt(rest, valueSchema.current);
                    cleanData = onCleaningProps(cleanData, valueSchema.current.properties);
                    obj[v.id] = cleanData;
                    return obj;
                }, {});
        },
        [updateOrder, onCleaningProps],
    );

    const onSaveDrawer = useCallback(() => {
        const id = state.data.id;
        validate(state.data, formFields.current).then(({ isValid, errors, cleanData }) => {
            if (!isValid) dispatch({ type: 'setErrors', errors });
            else {
                dispatch({ type: 'setSaving', value: true });

                // Removing schema from whole data
                let { schema, ...data } = cleanData;

                if (!data.name) {
                    data.name = data.description
                        .replace(/ /g, '_')
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .replace(/[^a-z_]/g, '');
                }

                if (data.isNew && data.name?.match(/^tbl/)) {
                    data.name = `Z_${data.name}`;
                } else if (data.isNew && !data.name?.match(/^Z_tbl/)) {
                    data.name = `Z_tbl${data.name}`;
                }

                data.createFields = createFields.current.map(
                    ({ id, isNew, schema, isTouched, ...rest }) =>
                        flatList(rest, valueSchema.current),
                );

                data.updateFields = Object.keys(updateFields.current).reduce((obj, key) => {
                    obj[key] = flatList(updateFields.current[key], valueSchema.current);
                    return obj;
                }, {});
                data.deleteFields = deleteFields.current;

                // Add integrationField value (convert from object to value if needed)
                data.integrationField =
                    state.data?.integrationField?.value ?? state.data?.integrationField ?? null;

                const pr = data.isNew ? createTable(data) : updateTable(id, data);

                pr.then((result) => {
                    const idList = result?.data?.id;
                    refreshList && refreshList();
                    setActiveRowId && setActiveRowId(null);
                    dispatch({ type: 'close' });
                    successToast({
                        t,
                        text: t(
                            data.isNew
                                ? 'cfm_success_create_value_list'
                                : 'cfm_success_update_value_list',
                        ),
                    });

                    const finalData = { idList, ...data };
                    onStack && onStack(stackIndex);
                    onSave && onSave(finalData);
                    onUpdate && onUpdate();
                }).catch((err) => {
                    console.error(err);
                    dispatch({ type: 'setSaving', value: false });
                    showErrorToast(t, err);
                });
            }
        });
    }, [state.data, refreshList, setActiveRowId, t, onStack, stackIndex, onSave, onUpdate]);

    const onCancelDrawer = useCallback(() => {
        dispatch({ type: 'close' });
        onStack && onStack(stackIndex);
        setActiveRowId && setActiveRowId(null);
        onCancel && onCancel();
    }, [onCancel, onStack, stackIndex, setActiveRowId]);

    const modifyFieldProps = useCallback(
        (field) => {
            let newField = field;
            switch (field.schemaId || field.id) {
                case 'isEditable':
                    newField = {
                        ...newField,
                        isHidden: !permissions?.extraFields?.userUpdate || overridePermissions,
                    };
                    break;
                default:
                    break;
            }
            return newField;
        },
        [overridePermissions, permissions?.extraFields?.userUpdate],
    );
    const onChange = useCallback(
        (data, field) => {
            let finalData = { ...data };
            if (field === 'name') {
                finalData = {
                    ...finalData,
                    name: normalizeFieldName(data?.name, null, false),
                };
                return dispatch({ type: 'changeData', data: finalData });
            } else if (field === 'description' && fieldsToHide.includes('name') && !data.id) {
                finalData = {
                    ...finalData,
                    name: normalizeFieldName(data?.description, null, false),
                };
                return dispatch({ type: 'changeData', data: finalData });
            }

            return dispatch({ type: 'changeData', data });
        },
        [fieldsToHide],
    );

    const onChangeValues = useCallback(
        (values) => {
            updateOrder({ values });
            const finalIds = values.map((el) => el.id).sort();

            if (state.data?.isNew) {
                dispatch({
                    type: 'changeIds',
                    originalIds: originalIds?.sort(),
                    ids: finalIds,
                });
                return (createFields.current = values);
            }
            createFields.current = values.filter((v) => v.isNew);
            updateFields.current = values
                .filter((v) => v.isTouched && !v.isNew)
                .reduce((obj, v) => {
                    const { isTouched, id, ...rest } = v;
                    obj[v.id] = rest;
                    return obj;
                }, {});
            dispatch({
                type: 'changeIds',
                originalIds: originalIds.sort(),
                ids: finalIds,
            });
        },
        [state.data, originalIds, updateOrder],
    );

    const onRemoveValues = useCallback(
        (id) => {
            let finalIds = [...ids];
            createFields.current = createFields.current.filter((field) => field.id !== id);
            delete updateFields.current[id];
            deleteFields.current = [...deleteFields.current, id];
            finalIds = finalIds.filter((field) => field !== id).sort();
            updateOrder({ id });

            dispatch({
                type: 'changeData',
                data: state.data,
                ids: finalIds,
                originalIds: originalIds?.sort(),
            });
        },
        [state.data, ids, originalIds, updateOrder],
    );

    const onStackDrawer = useCallback(
        (index) => {
            setStackIndex(index);
            onStack && onStack(index + 1);
        },
        [onStack],
    );

    const locales = useMemo(
        () => ({
            title: state.data.isNew
                ? t('cfm_title_create_value_list')
                : t('cfm_title_update_value_list'),
            cancel: t('cfm_action_cancel'),
            save: t('cfm_action_save'),
        }),
        [state.data.isNew, t],
    );

    const showAdviceRelatedFields =
        !fieldsToHide?.length &&
        state.data.associatedFields &&
        !!Object.keys(state.data.associatedFields).length;

    const handleIntegrationFieldChange = useCallback(
        (value) => {
            dispatch({
                type: 'changeData',
                data: { ...state.data, integrationField: value },
            });
        },
        [state.data],
    );

    const integrationFieldValue = useMemo(() => {
        if (!state.data?.integrationField) return null;
        if (typeof state.data.integrationField === 'object') {
            return state.data.integrationField;
        }
        return integrationFieldOptions?.find((opt) => opt.value === state.data.integrationField);
    }, [state.data?.integrationField, integrationFieldOptions]);

    const isIntegrationFieldReadOnly = !permissions?.extraFields?.updateIntegrationField;

    return (
        <HyperDrawer
            stackIndex={stackIndex}
            isOpen={state.isOpen}
            locales={locales}
            isLoading={state.isLoading}
            isSaving={state.isSaving}
            onCancel={onCancelDrawer}
            onSave={onSaveDrawer}
            data={state.validationData}
            originalData={state.originalData}
            className="valuelist__crud__modal"
        >
            <ForceForm
                onChange={onChange}
                values={state.data}
                errors={state.errors}
                schema={formFields.current}
                mode={state.data && state.data.id ? 'update' : 'create'}
                isWeb={isWeb || false}
                modifyFieldProps={modifyFieldProps}
            />
            {showAdviceRelatedFields && (
                <AdviceRelatedFields fields={state.data.associatedFields} />
            )}
            {isSage && (
                <Section
                    title={t('label_cfm_integration_configuration')}
                    overrides={{ root: { style: integrationSectionStyles.root } }}
                >
                    <Select
                        label={t('label_integration_direction')}
                        placeholder={t('label_selectone')}
                        options={integrationFieldOptions}
                        value={integrationFieldValue}
                        onChange={handleIntegrationFieldChange}
                        isReadOnly={isIntegrationFieldReadOnly}
                        isClearable
                    />
                </Section>
            )}
            {state.data && (
                <ValueListValues
                    valueListName={state.data?.name}
                    valueListId={state.id}
                    list={state.data}
                    schema={valueSchema.current}
                    onChange={onChangeValues}
                    onRemove={onRemoveValues}
                    onStack={onStackDrawer}
                    setIds={setIds}
                    setOriginalIds={setOriginalIds}
                    timeZone={timeZone}
                    overrides={overrides}
                    getInitialOrder={updateOrder}
                    onChangeOrder={onChangeOrder}
                    isForDuplicate={state.isForDuplicate}
                />
            )}
        </HyperDrawer>
    );
};

FormValueList.propTypes = {};

export default memo(FormValueList);
